import { endpoints } from '@api'
import { ArrowRightIcon, MagicWand, Refresh } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Drawer as ChakraDrawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  Flex,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack
} from '@chakra-ui/react'
import { NoteSettings } from '@components/icons'
import { useExperienceManager } from '@hooks'
import { useStoreState } from 'easy-peasy'
import { capitalize } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import type { StoreModel } from 'src/store/types'
import {
  getSessionControllerGetProgressNotesForSessionQueryKey,
  useProgressNoteTemplateMemoryControllerFindByNoteType
} from '~/clinician-api'
import {
  MemoryManagementDrawer,
  ReadOnlyMemories
} from './progress-note-template-memory'

const getPreferenceLabel = (value: string, options: any) => {
  return options.find((o: any) => o.value === value)?.label
}

const ReminderIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.4998 8H7.4998V7C7.4998 6.71667 7.40397 6.47917 7.2123 6.2875C7.02064 6.09583 6.78314 6 6.4998 6C6.21647 6 5.97897 6.09583 5.7873 6.2875C5.59564 6.47917 5.4998 6.71667 5.4998 7C5.4998 7.28333 5.59564 7.52083 5.7873 7.7125C5.97897 7.90417 6.21647 8 6.4998 8ZM10.9998 8C11.2831 8 11.5206 7.90417 11.7123 7.7125C11.904 7.52083 11.9998 7.28333 11.9998 7C11.9998 6.71667 11.904 6.47917 11.7123 6.2875C11.5206 6.09583 11.2831 6 10.9998 6C10.7165 6 10.479 6.09583 10.2873 6.2875C10.0956 6.47917 9.9998 6.71667 9.9998 7V8H10.9998ZM10.4748 22C10.0081 22 9.57064 21.9 9.1623 21.7C8.75397 21.5 8.40814 21.2167 8.1248 20.85L2.6748 13.925L3.1498 13.425C3.48314 13.075 3.88314 12.8667 4.3498 12.8C4.81647 12.7333 5.2498 12.825 5.6498 13.075L7.4998 14.2V10H6.4998C5.66647 10 4.95814 9.70833 4.3748 9.125C3.79147 8.54167 3.4998 7.83333 3.4998 7C3.4998 6.16667 3.79147 5.45833 4.3748 4.875C4.95814 4.29167 5.66647 4 6.4998 4C6.68314 4 6.85397 4.01667 7.0123 4.05C7.17064 4.08333 7.33314 4.125 7.4998 4.175V3C7.4998 2.71667 7.59564 2.47917 7.7873 2.2875C7.97897 2.09583 8.21647 2 8.4998 2C8.78314 2 9.0248 2.09583 9.2248 2.2875C9.4248 2.47917 9.5248 2.71667 9.5248 3V4.4C9.75814 4.26667 9.99564 4.16667 10.2373 4.1C10.479 4.03333 10.7331 4 10.9998 4C11.8331 4 12.5415 4.29167 13.1248 4.875C13.7081 5.45833 13.9998 6.16667 13.9998 7C13.9998 7.83333 13.7081 8.54167 13.1248 9.125C12.5415 9.70833 11.8331 10 10.9998 10H9.5248V17.8L7.0998 16.3L9.6998 19.625C9.7998 19.7417 9.91647 19.8333 10.0498 19.9C10.1831 19.9667 10.3248 20 10.4748 20H15.9998C16.5498 20 17.0206 19.8042 17.4123 19.4125C17.804 19.0208 17.9998 18.55 17.9998 18V14C17.9998 13.7167 17.904 13.4792 17.7123 13.2875C17.5206 13.0958 17.2831 13 16.9998 13H11.5248V11H16.9998C17.8331 11 18.5415 11.2917 19.1248 11.875C19.7081 12.4583 19.9998 13.1667 19.9998 14V18C19.9998 19.1 19.6081 20.0417 18.8248 20.825C18.0415 21.6083 17.0998 22 15.9998 22H10.4748Z"
        fill="#2D54E8"
      />
    </svg>
  )
}

const ExampleMemoryInstructions = [
  'Refer to the client by their first name',
  'Refer to the provider as "therapist"',
  'Document all the symptoms of the client, include frequency and severity of each symptom',
  'Do not use any direct quotes anywhere in the note',
  'Include every intervention the therapist uses in the session, detail clinical actions and techniques',
  'Ensure the tone of the note is objective and professional'
]

const TextAreaAutoHeight = (props: any) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto'
      ref.current.style.height = `${ref.current.scrollHeight}px`
    }
  }, [props.value]) // Resize when the value changes

  return <Textarea ref={ref} resize="none" overflowY="hidden" {...props} />
}

export const MagicEditDrawer = ({
  isOpen,
  closeDrawer,
  noteType,
  noteTypeLabel,
  progressNoteId,
  preferences = {},
  openSettings,
  session,
  progressNote
}: {
  isOpen: boolean
  closeDrawer: () => void
  progressNoteId: string
  preferences: Record<string, any>
  openSettings: () => void
  noteType: string
  noteTypeLabel?: string
  session: any
  progressNote: any
}) => {
  const exampleInstructionsRef = useRef<HTMLDivElement>(null)
  const { user } = useStoreState((state: StoreModel) => state.auth)
  const { notePreferencesEnabled, memoryEnabled } = useExperienceManager()

  const { sessionId }: { sessionId: string } = useParams()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [userInstructions, setUserInstructions] = useState('')
  const [saveToMemory, setSaveToMemory] = useState(false)
  const [isManagingMemory, setIsManagingMemory] = useState(false)
  const [hideExampleInstructions, setHideExampleInstructions] = useState(
    !!localStorage.getItem('hideExampleInstructions')
  )

  useEffect(() => {
    const element = exampleInstructionsRef.current

    if (!element) return

    const handleWheel = (e: WheelEvent) => {
      if (!element) return
      e.preventDefault()
      if (e.deltaX !== 0) {
        element.scrollLeft += e.deltaX
      } else {
        element.scrollLeft += e.deltaY
      }
    }

    element.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      element.removeEventListener('wheel', handleWheel)
    }
  }, [exampleInstructionsRef?.current, hideExampleInstructions, memoryEnabled])

  const organizationId = user?.clinic?.organization?.id

  const { data, isLoading: isSettingsLoading } = useQuery(
    [endpoints.getProgressNoteSettings.getCacheId(), organizationId],
    () => endpoints.getProgressNoteSettings.request({ organizationId })
  )

  const { data: memories = [], isLoading: isMemoriesLoading } =
    useProgressNoteTemplateMemoryControllerFindByNoteType({
      noteType
    })

  const { mutateAsync: executeRegenerateProgressNote, isLoading } = useMutation(
    endpoints.postRegenerateProgressNote.request,
    {
      onError: () => {
        toast({
          title: 'Error',
          description: 'There was an error regenerating this progress note',
          status: 'error',
          isClosable: true,
          duration: 2000,
          position: 'bottom-left'
        })
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
        )
        // eslint-disable-next-line
        parent.postMessage(
          {
            type: 'BP_GENERATE_NOTE_CLICKED',
            sessionId,
            progressNoteId,
            sessionType: progressNote?.template?.sessionType,
            noteType: noteType,
            treatmentApproaches: session?.treatmentApproaches
          },
          '*'
        )
      }
    }
  )

  const submitRegenerateProgressNote = useCallback(async () => {
    const data = {
      progressNoteId,
      userInstructions,
      saveToMemory
    }
    await executeRegenerateProgressNote(data)

    closeDrawer()
  }, [progressNoteId, userInstructions, saveToMemory])

  const handleGoToSettings = () => {
    closeDrawer()
    openSettings()
  }

  const handleCloseMemoryManagement = () => {
    setIsManagingMemory(false)
  }

  if (isSettingsLoading) return null

  // @ts-ignore
  const { preferenceOptions }: { preferenceOptions: any } = data

  return (
    <>
      <ChakraDrawer isOpen={isOpen} onClose={closeDrawer} size="md">
        <DrawerOverlay />
        <DrawerContent>
          {isManagingMemory ? (
            <MemoryManagementDrawer
              noteTypeLabel={noteTypeLabel}
              onClose={handleCloseMemoryManagement}
              noteType={noteType}
              sessionId={session.id}
            />
          ) : (
            <>
              <Box
                height="80px"
                backgroundColor="primary"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px="small"
              >
                <Flex w="150px" height="80px" align="center">
                  <MagicWand fill="white" />
                  <Text as="span" color="white" paddingLeft="xsmall">
                    Magic edit
                  </Text>
                </Flex>
                <DrawerCloseButton
                  color="white"
                  position="static"
                  _focus={{ outline: 'none' }}
                />
              </Box>
              <Box
                p="small"
                overflowY="scroll"
                height="100%"
                display="flex"
                justifyContent="space-between"
                flexDirection="column"
              >
                <VStack spacing="small" alignItems="flex-start">
                  <Text>
                    Want to personalize your note? Tell our AI what to update.
                  </Text>
                  <Box
                    position="relative"
                    w="100%"
                    border="1px solid"
                    borderColor="pale_gray"
                    borderRadius="md"
                    h="auto"
                    minH="140px"
                    p={4}
                  >
                    <TextAreaAutoHeight
                      p="0"
                      h="80px"
                      placeholder="Enter your instructions here..."
                      value={userInstructions}
                      _placeholder={{ color: 'medium_gray' }}
                      onChange={(e: any) => setUserInstructions(e.target.value)}
                      border="none"
                      _focus={{ outline: 'none', boxShadow: 'none' }}
                      resize="none"
                      mb={2}
                    />
                    {memoryEnabled &&
                      !hideExampleInstructions &&
                      !memories.length && (
                        <Stack spacing="8px">
                          <Flex justifyContent="space-between">
                            <Text color="medium_gray">
                              Not sure where to start? Try one of these!
                            </Text>
                            <Button
                              variant="link"
                              textDecoration="none"
                              color="medium_gray"
                              minWidth="auto"
                              margin="0"
                              _focus={{ outline: 'none' }}
                              onClick={() => {
                                localStorage.setItem(
                                  'hideExampleInstructions',
                                  'true'
                                )
                                setHideExampleInstructions(true)
                              }}
                            >
                              Hide
                            </Button>
                          </Flex>
                          <Flex
                            ref={exampleInstructionsRef}
                            overflowX="auto"
                            wrap="nowrap"
                            gap="8px"
                            style={{
                              marginLeft: '-16px',
                              marginRight: '-16px',
                              paddingLeft: '16px',
                              paddingRight: '16px',
                              scrollbarWidth: 'none'
                            }}
                          >
                            {ExampleMemoryInstructions.map(
                              (instruction, index) => (
                                <Box
                                  key={index}
                                  bg="#EEF2FF"
                                  p={3}
                                  borderRadius="16px"
                                  cursor="pointer"
                                  minW="182px"
                                  display="flex"
                                  alignItems="center"
                                  onClick={() => {
                                    setUserInstructions(
                                      userInstructions
                                        ? `${userInstructions}\n${instruction}`
                                        : instruction
                                    )
                                  }}
                                >
                                  <Text color="primary" fontSize="14px">
                                    {instruction}
                                  </Text>
                                </Box>
                              )
                            )}
                          </Flex>
                        </Stack>
                      )}
                    {memoryEnabled && hideExampleInstructions && (
                      <Flex justifyContent="flex-end" flex="1">
                        <Button
                          variant="link"
                          textDecoration="none"
                          color="medium_gray"
                          minWidth="auto"
                          margin="0"
                          onClick={() => {
                            localStorage.removeItem('hideExampleInstructions')
                            setHideExampleInstructions(false)
                          }}
                        >
                          Show Suggestions
                        </Button>
                      </Flex>
                    )}
                  </Box>
                  {memoryEnabled && (
                    <Box
                      width="100%"
                      paddingTop="16px"
                      paddingBottom="24px"
                      style={{ marginTop: '0px' }}
                    >
                      <Stack spacing={2}>
                        <RadioGroup
                          value={saveToMemory ? 'true' : 'false'}
                          onChange={value => setSaveToMemory(value === 'true')}
                        >
                          <Stack spacing={2}>
                            <Radio
                              _focus={{ outline: 'none' }}
                              borderColor="light_gray"
                              id="applyToThisNote"
                              value="false"
                            >
                              Apply to this note only.
                            </Radio>
                            <Radio
                              _focus={{ outline: 'none' }}
                              borderColor="lightgray"
                              id="applyToAllNotes"
                              value="true"
                              isDisabled={progressNote.isMemoryDisabled}
                            >
                              {`Apply to this note and all future ${noteTypeLabel} notes for all clients.`}
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      </Stack>
                    </Box>
                  )}
                  {memoryEnabled && (
                    <>
                      <Button
                        colorScheme="primary"
                        variant="solid"
                        type="submit"
                        onClick={submitRegenerateProgressNote}
                        isFullWidth
                        size="lg"
                        m={0}
                        style={{
                          marginTop: '0px',
                          marginBottom: '24px'
                        }}
                        leftIcon={<Refresh fill="White" h="16px" w="16px" />}
                        isLoading={isLoading}
                        isDisabled={!userInstructions.trim()}
                      >
                        Generate new note
                      </Button>
                      <ReadOnlyMemories
                        isDisabled={progressNote.isMemoryDisabled}
                        noteTypeLabel={noteTypeLabel}
                        noteType={noteType}
                        onManageClick={() => setIsManagingMemory(true)}
                      />
                    </>
                  )}
                </VStack>
                {notePreferencesEnabled && !memoryEnabled && (
                  <Box bg="#2D54E80A" borderRadius="8px" py={4} px={5} w="100%">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center">
                        <NoteSettings fill="black" />
                        <Text fontWeight="bold" ml={2}>
                          Your note preferences
                        </Text>
                      </Box>
                      <Box>
                        <Button
                          color="primary"
                          onClick={handleGoToSettings}
                          ml={2}
                          alignSelf="flex-end"
                          variant="link"
                          textDecoration="none"
                          rightIcon={<ArrowRightIcon fill="primary" />}
                          sx={{
                            '.chakra-button__icon': {
                              marginLeft: 0,
                              position: 'relative',
                              top: '1px'
                            }
                          }}
                          _focus={{ outline: 'none' }}
                        >
                          Go to settings
                        </Button>
                      </Box>
                    </Box>
                    <Flex gap="16px" mt={4} lineHeight="18px">
                      <Box flex="1">
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Terminology:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.selfReference,
                              preferenceOptions?.selfReference?.options
                            )}
                            {', '}
                            {getPreferenceLabel(
                              preferences.patientReference,
                              preferenceOptions?.patientReference?.options
                            )}
                          </Text>
                        </Box>
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Length of note:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.noteLength,
                              preferenceOptions?.noteLength?.options
                            )}
                          </Text>
                        </Box>
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Medical Necessity:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.medicalNecessity,
                              preferenceOptions?.medicalNecessity?.options
                            )}
                          </Text>
                        </Box>
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Diagnostic Symptoms:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.diagnosticSymptoms,
                              preferenceOptions?.diagnosticSymptoms?.options
                            )}
                          </Text>
                        </Box>
                      </Box>
                      <Box flex="1">
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Action Items:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.actionItems,
                              preferenceOptions?.actionItems?.options
                            )}
                          </Text>
                        </Box>
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Direct Quotes:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {getPreferenceLabel(
                              preferences.directQuotes,
                              preferenceOptions?.directQuotes?.options
                            )}
                          </Text>
                        </Box>
                        {/* <Box flex="1">
                        <Text fontSize="13px" as="span" fontWeight="bold">
                          Formatting:
                        </Text>
                        <Text fontSize="13px" as="span" ml={1}>
                          {getPreferenceLabel(
                            preferences.formatting,
                            preferenceOptions?.formatting?.options
                          )}
                        </Text>
                      </Box> */}
                        <Box flex="1">
                          <Text fontSize="13px" as="span" fontWeight="bold">
                            Mental Status Exam:
                          </Text>
                          <Text fontSize="13px" as="span" ml={1}>
                            {preferences.mentalStatusExam.length === 0
                              ? 'None'
                              : preferences.mentalStatusExam
                                  .map((i: string) => capitalize(i))
                                  .join(', ')}
                          </Text>
                        </Box>
                      </Box>
                    </Flex>
                  </Box>
                )}
                {!memoryEnabled && (
                  <Box px={4}>
                    <Text align="center" fontWeight="bold" mb={2}>
                      Example instructions
                    </Text>
                    <List styleType="disc">
                      <ListItem fontStyle="italic">
                        Include [specific topics discussed not yet in note]
                      </ListItem>
                      <ListItem fontStyle="italic">
                        Include [specific client quote not yet in note]
                      </ListItem>
                      <ListItem fontStyle="italic">
                        Detail [observations you have of the client]
                      </ListItem>
                      <ListItem fontStyle="italic">
                        Document [specifics not discussed in-session]
                      </ListItem>
                      <ListItem fontStyle="italic">
                        Include [client actions outside of session]
                      </ListItem>
                    </List>
                  </Box>
                )}
              </Box>
              {!memoryEnabled && (
                <DrawerFooter width="100%" px="small" py={0}>
                  <Box w="100%">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="100%"
                      py={6}
                    >
                      <Button
                        colorScheme="primary"
                        variant="solid"
                        type="submit"
                        onClick={submitRegenerateProgressNote}
                        isFullWidth
                        size="lg"
                        m={0}
                        leftIcon={<Refresh fill="White" h="16px" w="16px" />}
                        isLoading={isLoading}
                        isDisabled={!userInstructions.trim()}
                      >
                        Generate new note
                      </Button>
                    </Box>
                  </Box>
                </DrawerFooter>
              )}
            </>
          )}
        </DrawerContent>
      </ChakraDrawer>
    </>
  )
}
